import { useEffect, useRef, useState } from 'react';
import { MOCK, isSchemaPending } from '../lib/supabase.js';
import * as db from '../lib/data.js';
import { toUploadableImage } from '../lib/image.js';
import { Modal, Fld, CountrySelect } from '../components/Modals.jsx';
import MaterialPicker from '../components/MaterialPicker.jsx';
import DbPending from '../components/DbPending.jsx';
import SiblingFill from '../components/SiblingFill.jsx';
import { FILLABLE, changedFields } from '../lib/siblings.js';
import { itemName, itemNameTh, itemTitle, hasThai, thaiNameState } from '../lib/naming.js';
import NameTh from '../components/NameTh.jsx';
import ProductFiles from '../components/ProductFiles.jsx';
import { useLeaveGuard } from '../lib/leaveguard.js';
import MaterialEnqueueModal, { useMaterialsReady } from '../components/MaterialEnqueueModal.jsx';
import { isStructuredMaterial, safeHref, typedByPerson } from '../lib/materials.js';

// TASK 3 — Product Classification master: ทำครั้งเดียว reuse ทุก shipment
// broker ตี HS กลับ → กรอก + ติ๊กยืนยัน → ครั้งหน้าไม่ต้องทำ dossier ซ้ำ

const EMPTY = {
  sku: '', brand: '', category: '', name_en: '', name_th: '', what_is_it: '', material: '',
  usage_desc: '', origin_country: '', power_spec: '', hs_code: '',
  hs_confirmed: false, is_dangerous_goods: false,
};

function completeness(c) {
  const need = ['name_en', 'name_th', 'what_is_it', 'material', 'usage_desc', 'origin_country', 'brand'];
  // ชื่อไทยนับเฉพาะที่เป็นภาษาไทยจริง — กติกาเดียวกับ db.cardGaps / ด่าน "ครบพร้อมส่ง broker"
  const have = need.filter(k => (k === 'name_th' ? hasThai(c[k]) : (c[k] ?? '').trim())).length;
  const photos = (c.photos ?? []).length > 0 ? 1 : 0;
  return { have: have + photos, total: need.length + 1 };
}

// ค่าที่ลงมาจากหน้าตรวจผลแต่ไม่มีลิงก์แหล่ง = AI หาไม่เจอ คนพิมพ์เอง (แถว not_found) — ห้ามเรียกว่า "จาก AI"
//   ไม่มีคอลัมน์บอกตรง ๆ บนใบสินค้า: แถว not_found ล้างลิงก์เสมอ · ผล AI ที่ยืนยันมีลิงก์เสมอ ยกเว้นลิงก์ไม่ใช่ https ที่ถูกล้างตอนค้น

// บรรทัด "ค่านี้มาจากไหน?" ใต้ช่องวัสดุ — โชว์เฉพาะค่าที่ลงมาจากหน้าตรวจผล (ฟอร์มไม่เคยส่ง 3 คอลัมน์นี้)
//   แก้ช่องวัสดุแล้ว trigger ฝั่งฐานข้อมูลล้างค่าอังกฤษ+ลิงก์ให้เอง → บอกก่อนบันทึก จะได้ไม่ตกใจว่าหาย
function MaterialProvenance({ item, material }) {
  const en = String(item?.material_en ?? '').trim();
  const url = String(item?.material_source_url ?? '').trim();
  if (!item?.material_research_id && !en && !url) return null;
  const href = safeHref(url);
  const changed = String(material ?? '').trim() !== String(item?.material ?? '').trim();
  return (
    <div className="mat-prov">
      <b>ค่านี้มาจากไหน?</b> {typedByPerson(item) ? 'คนพิมพ์เองที่หน้าตรวจผล — AI หาแหล่งไม่เจอ' : 'หาวัสดุด้วย AI แล้วคนกดยืนยัน'}
      {en && <> · อังกฤษตามผู้ผลิต: {en}</>}
      {url && <> · แหล่ง: {href
        ? <a href={href} target="_blank" rel="noopener noreferrer">{url}</a>
        : url}</>}
      {changed && <div className="mat-prov-warn">แก้ช่องวัสดุแล้ว — ตอนบันทึก ระบบจะล้าง{en || url ? 'ค่าอังกฤษและลิงก์แหล่งนี้' : 'ที่มาของค่านี้'}ให้ เพราะไม่ตรงกับค่าใหม่แล้ว</div>}
    </div>
  );
}

function EditModal({ item, brands = [], cats = [], canCost = false, onClose, onSaved, onOpenPermit, onDeleted }) {
  const [f, setF] = useState({ ...EMPTY, ...item });
  // ⚠ ทุกการเขียน f ใช้รูป setF(prev => …) — เดิมเขียน { ...f, x } จากค่าตอนเริ่ม แล้วงานที่จบทีหลัง
  //   (อัปรูป / เพิ่มวัสดุใหม่ที่รอเซิร์ฟเวอร์) เขียนทับสิ่งที่กรอกระหว่างรอ — พิสูจน์ในฮาร์เนส 26 ต.ค. 69
  const fRef = useRef(f);
  fRef.current = f;
  // ข้อมูลที่ยังไม่บันทึก (ไม่นับรูป — รูปบันทึกทันทีตอนอัป)
  const strip = ({ photos, ...rest }) => JSON.stringify(rest);
  const initialRef = useRef(strip({ ...EMPTY, ...item }));
  const dirty = strip(f) !== initialRef.current;
  const [askClose, setAskClose] = useState(false);
  const [matPending, setMatPending] = useState('');   // คำในช่องวัสดุที่พิมพ์ค้างไว้ ยังไม่ได้ใส่
  const [matText, setMatText] = useState(() => isStructuredMaterial(item?.material));   // ช่องวัสดุเป็นข้อความอยู่ไหม (ค่าจริงมาจาก MaterialPicker)
  const formRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [savedCard, setSavedCard] = useState(null);   // บันทึกแล้ว → เสนอเติมให้รุ่นเดียวกัน
  const [savedBefore, setSavedBefore] = useState(null); // หน้าตาก่อนแก้ — เฉพาะช่องที่คนนี้แก้ (ให้ SiblingFill)
  // กำลังแนบไฟล์อยู่ → ห้ามปิด/บันทึก (ปิดกลางทาง = ไฟล์อัปต่อเบื้องหลังแต่ถ้าพังจะไม่มีใครเห็น)
  const [filesBusy, setFilesBusy] = useState(false);
  const [sibBusy, setSibBusy] = useState(false);       // แผงเปลี่ยนตามกำลังเขียน → ห้ามปิด (ปิดกลางทาง = ผล/รายชื่อส่ง broker หาย)
  // ปุ่มย้อนกลับของเบราว์เซอร์ / รีเฟรช / ปิดแท็บ ตอนหน้านี้เปิดอยู่ → ถามก่อน (เงื่อนไขเดียวกับ safeClose)
  useLeaveGuard(
    filesBusy ? 'กำลังแนบไฟล์อยู่ — ออกตอนนี้ไฟล์อาจแนบไม่ครบ ออกเลยไหม?'
    : sibBusy || busy ? 'กำลังบันทึกอยู่ — ออกตอนนี้อาจบันทึกไม่ครบ ออกเลยไหม?'
    : dirty && !savedCard ? 'ยังไม่ได้บันทึกที่แก้ไว้ในหน้าแก้ไขสินค้า — ออกจากหน้านี้เลยไหม? (ที่แก้ไว้จะหาย)'
    : null);
  // ปิดด้วย ✕ / ปิด / พื้นหลัง → ถ้ามีที่แก้แล้วยังไม่บันทึก ถามก่อน (เดิมปิดทิ้งเงียบ ๆ)
  const safeClose = () => {
    if (filesBusy) { setError('กำลังแนบไฟล์อยู่ — รอให้เสร็จก่อนแล้วค่อยปิด'); return; }
    if (sibBusy) return;   // กำลังบันทึกให้รุ่นเดียวกัน — ปุ่มในแผงขึ้น "กำลังบันทึก…" อยู่แล้ว
    if (dirty && !savedCard) { setAskClose(true); return; }
    onClose();
  };
  const del = async () => {
    if (MOCK) { setError('โหมด MOCK — ทดสอบหน้าตาเท่านั้น'); setConfirmDel(false); return; }
    setBusy(true); setError(null);
    try { await db.deleteClassification(item.id); onDeleted?.(); onClose(); }
    catch (er) { setError(er.message ?? String(er)); setBusy(false); setConfirmDel(false); }
  };
  const [pdMsg, setPdMsg] = useState(null);   // สถานะค้น Product Manager
  const [pdResults, setPdResults] = useState([]);
  const [pdOpen, setPdOpen] = useState(false);
  const searchTimer = useRef(null);
  const isNew = !item?.id;
  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setF(prev => ({ ...prev, [k]: v }));
  };

  // Enter ในช่องกรอก = ไปช่องถัดไป (เดิมเบราว์เซอร์ถือว่า "กดบันทึก" → บันทึกครึ่ง ๆ กลาง ๆ แล้วหน้าต่างปิด
  //   — เป็นต้นเหตุ "กรอกอยู่แล้วปิดไปเอง" ที่ user แจ้ง) · Ctrl/⌘+Enter = บันทึกจริง
  //   ช่องที่จัดการ Enter เองแล้ว (ช่องวัสดุ / หมายเหตุไฟล์) เรียก preventDefault ไว้ → ข้ามตรงนี้
  const onFormKeyDown = (e) => {
    if (e.key !== 'Enter' || e.defaultPrevented || e.nativeEvent?.isComposing) return;
    const t = e.target;
    if (t.tagName !== 'INPUT' || ['checkbox', 'radio', 'file', 'button', 'submit'].includes(t.type)) return;
    e.preventDefault();
    if (e.metaKey || e.ctrlKey) {
      const form = e.currentTarget;
      if (form.requestSubmit) form.requestSubmit();
      else form.querySelector('button:not([type=button])')?.click();   // Safari < 16
      return;
    }
    const fields = [...e.currentTarget.querySelectorAll(
      'input:not([type=hidden]):not([type=file]):not([disabled]), select:not([disabled]), textarea:not([disabled])')]
      .filter(el => el.offsetParent !== null);
    const next = fields[fields.indexOf(t) + 1];
    if (next) next.focus(); else t.blur();
  };

  // เลือกสินค้าจาก Product Manager → "ทับ" ข้อมูลตามสินค้าที่เลือก (เปลี่ยน SKU ข้อมูลเปลี่ยนตาม
  // ไม่ค้างของเก่า) + ดึงรูปจาก PD มาใช้เลย
  const pickPd = (r) => {
    setF(prev => ({
      ...prev,
      sku: r.sku ?? prev.sku,
      // ชื่อไทยเอาเฉพาะที่เป็นภาษาไทยจริง — เดิมก๊อปชื่ออังกฤษใส่ช่องไทย (2,708 ใบช่องไทยเป็นอังกฤษ นับ 28 ต.ค. 69)
      //   ปล่อยว่างแล้วช่อง "ชื่อไทยที่จะสำแดง" (required) จะขอให้คนพิมพ์ชื่อไทยก่อนบันทึก
      name_th: hasThai(r.name_th) ? r.name_th : '',
      name_en: r.name_en || r.name_th || r.sku || '',
      brand: db.pdBrandCategory(r).brand || '',
      category: db.pdBrandCategory(r).category || '',
      photos: r.image_url ? [{ url: r.image_url, caption: 'PD' }] : prev.photos,
    }));
    setPdOpen(false); setPdResults([]);
    setPdMsg(`ดึงจาก Product Manager: ${itemName(r)}`);
  };

  // พิมพ์ SKU/ชื่อ (บางส่วนก็ได้) → ค้น PD แบบ ilike แสดงรายการให้เลือก (พิมพ์ไม่ครบก็เจอ)
  const onSkuChange = (e) => {
    const v = e.target.value;
    setF(prev => ({ ...prev, sku: v }));
    if (MOCK || !isNew) return;
    setPdMsg(null);
    clearTimeout(searchTimer.current);
    const term = v.trim();
    if (term.length < 2) { setPdResults([]); setPdOpen(false); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const res = await db.searchPdProducts(term);
        setPdResults(res.slice(0, 8)); setPdOpen(res.length > 0);
        setPdMsg(res.length ? null : 'ไม่พบใน Product Manager — กรอกเอง');
      } catch { /* เงียบ */ }
    }, 250);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (filesBusy) return;   // ปุ่มบันทึกถูกปิดอยู่แล้ว — กันกด Enter ในช่องอื่นด้วย
    // ช่องวัสดุมีคำพิมพ์ค้างที่ยังไม่ได้ใส่ → หยุดและบอก (เดิมบันทึกไปโดยไม่มีคำนั้น = "ใส่ material แล้วไม่จำ")
    if (matPending) {
      setError(`ช่อง "ทำจากวัสดุอะไร?" ยังมีคำว่า "${matPending}" ที่ยังไม่ได้ใส่ — `
        + 'เลือกจากรายการ หรือคลิกช่องแล้วกด Enter เพื่อเพิ่มเป็นวัสดุใหม่ แล้วค่อยกดบันทึก');
      formRef.current?.querySelector('.matpick input')?.focus();
      return;
    }
    // ชื่อไทยที่สำแดง = คำไทยที่บอกว่าสินค้าคืออะไร (user 29 ต.ค. 69 "Landlock ชื่อไทยคือเต็นท์")
    //   เดิมรับชื่ออังกฤษในช่องนี้ได้ → 2,700 ใบกลายเป็นไม่มีชื่อไทยให้ศุลกากร
    if (!hasThai(f.name_th)) {
      setError('ช่อง "ชื่อไทยที่จะสำแดง" ต้องเป็นคำภาษาไทยที่บอกว่าสินค้าคืออะไร เช่น เต็นท์ · เก้าอี้พับ · เสื้อยืด'
        + ' — ศุลกากรใช้ช่องนี้ ชื่อรุ่นภาษาอังกฤษใส่ช่องชื่อสินค้า (อังกฤษ) ได้เลย');
      formRef.current?.querySelector('input[name="name_th"]')?.focus();
      return;
    }
    if (MOCK) { setError('โหมด MOCK — ทดสอบหน้าตาเท่านั้น (ของจริงบันทึกได้หลัง login)'); return; }
    setBusy(true); setError(null);
    try {
      // permit_docs ไม่ส่ง: ฟอร์มนี้ไม่ได้แก้ช่องนั้น ส่งไปเท่ากับเอาสำเนาตอนเปิดใบไปทับ
      // ข้อความ/เลข มอก. ที่หน้าเอกสารยื่นขอใบอนุญาตบันทึกไว้ทีหลัง (รีวิว 26 ต.ค.)
      // 3 คอลัมน์ที่มาของวัสดุไม่ส่งเด็ดขาด: เขียนได้ทาง RPC ยืนยันผล AI เท่านั้น (ค่าเก่าในฟอร์มจะทับหรือปลอมที่มา)
      const { id, photos, created_at, updated_at, product_id, permit_docs,
        material_en, material_source_url, material_research_id, ...fields } = f;
      for (const k of Object.keys(fields)) if (fields[k] === '') fields[k] = null;
      if (fields.origin_country) fields.origin_country = fields.origin_country.toUpperCase();
      // วัสดุ: ตัดช่องว่าง/บรรทัดเปล่าหัวท้ายก่อนเทียบและก่อนส่ง — ช่องข้อความกด Enter เพิ่มบรรทัดได้
      //   ถ้าปล่อยไป ฐานข้อมูลนับว่าค่าเปลี่ยน แล้ว trigger ล้างค่าอังกฤษ+ลิงก์ AI ทิ้งทั้งที่ข้อความเหมือนเดิม · ว่างล้วน = null
      const cleanMaterial = (v) => String(v ?? '').trim() || null;
      fields.material = cleanMaterial(fields.material);
      let saved, before = item;
      if (isNew) {
        if (Array.isArray(photos) && photos.length) fields.photos = photos;  // รูปจาก PD ตอนสร้างใหม่
        saved = await db.saveClassification(fields, null);
      } else {
        // ใบเดิม = ส่งเฉพาะช่องที่แก้ (แบบ EditConsignmentModal) — ฟอร์มเปิดจากรายการที่โหลดไว้นานแล้วได้
        //   ถ้าส่งทุกช่อง คนกรอกแค่วิธีใช้ก็เอาวัสดุสำเนาเก่า (มักเป็นค่าว่าง) ไปทับค่าที่เพิ่งยืนยันจาก AI (รีวิว 1 พ.ย. 69)
        const norm = (k, v) => {
          if (k === 'material') return JSON.stringify(cleanMaterial(v));
          const x = v === '' || v === undefined ? null : v;
          return JSON.stringify(k === 'origin_country' && x ? String(x).toUpperCase() : x);
        };
        const base = { ...EMPTY, ...item };
        const changed = {};
        for (const k of Object.keys(fields)) if (norm(k, fields[k]) !== norm(k, base[k])) changed[k] = fields[k];
        // ไม่ได้แก้อะไร = ไม่ยิงเขียน แต่ยังอ่านค่าล่าสุดให้แผงรุ่นเดียวกัน (ไม่เอาสำเนาเก่าไปเติมให้ใบอื่น)
        saved = Object.keys(changed).length
          ? await db.saveClassification(changed, item.id)
          : (await db.getClassification(item.id).catch(() => null)) ?? item;
        // ช่องที่คนอื่นเปลี่ยนมาตั้งแต่โหลดรายการ ไม่นับว่า "คนนี้เพิ่งแก้" — ไม่งั้นแผงรุ่นเดียวกันชวนให้ส่งต่อค่าที่คนนี้ไม่ได้แตะ
        before = { ...saved };
        for (const k of Object.keys(changed)) before[k] = item[k];
      }
      onSaved(saved);
      // มีช่องที่รุ่นเดียวกันใช้ค่าเดียวกันได้ หรือเพิ่งแก้ช่องที่ให้รุ่นเดียวกันเปลี่ยนตามได้ (วัสดุ · ผลิตที่ · ชื่อ ฯลฯ)
      //   → ค้างหน้าไว้ถามก่อน (SiblingFill ปิดเองถ้าไม่มีรุ่นเดียวกัน)
      const hasShareable = FILLABLE.some(([k]) => String(saved?.[k] ?? '').trim());
      if (saved?.id && (hasShareable || changedFields(before, saved).length)) { setSavedBefore(before); setSavedCard(saved); setBusy(false); return; }
      onClose();
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };

  const onPhoto = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = '';
    if (!raw || !item?.id || MOCK) return;
    setBusy(true); setError(null);
    try {
      const file = await toUploadableImage(raw);   // แปลง/ย่อเป็น JPEG อัตโนมัติ กันอัพไม่ได้
      const url = await db.uploadClassPhoto(item.id, file);
      // อ่านรูปชุดล่าสุดหลังอัปเสร็จ (ไม่ใช่ชุดตอนเริ่ม) และเขียนเฉพาะช่องรูป — ไม่ทับที่กรอกระหว่างรอ
      const photos = [...(fRef.current.photos ?? []), { url, caption: file.name }];
      const saved = await db.saveClassification({ photos }, item.id);
      setF(prev => ({ ...prev, photos }));
      onSaved(saved);
    } catch (er) { setError(er.message ?? String(er)); }
    setBusy(false);
  };

  return (
    <Modal title={item?.id ? `แก้ไข — ${f.name_en || f.sku}` : 'เพิ่มสินค้าใหม่เข้า master'}
      sub="ข้อมูลชุดนี้คือสิ่งที่ broker ใช้ตีพิกัด — ยิ่งชัด ของยิ่งไม่โดนดอง" onClose={safeClose} wide>
      {askClose && (
        <div className="closeask" role="alertdialog">
          <span>ยังไม่ได้บันทึกที่แก้ไว้ — ปิดเลยไหม?</span>
          <button type="button" className="btn2 sm" onClick={() => setAskClose(false)} autoFocus>กลับไปแก้ต่อ</button>
          <button type="button" className="btn-del" onClick={() => { setAskClose(false); onClose(); }}>ปิดโดยไม่บันทึก</button>
        </div>
      )}
      {savedCard ? (
        <SiblingFill card={savedCard} before={savedBefore} onClose={onClose} onChanged={onSaved} onEmpty={onClose} onBusy={setSibBusy}/>
      ) : (
      <form onSubmit={submit} onKeyDown={onFormKeyDown} ref={formRef}>
        <div className="frow">
          <Fld label="SKU (ตรงกับระบบขาย/สั่งซื้อ)" hint={isNew ? 'พิมพ์บางส่วนของ SKU หรือชื่อก็ค้นเจอ — เลือกจากรายการ ระบบดึงชื่อ/แบรนด์/รูปให้เอง' : undefined}>
            <div className="pd-suggest-wrap">
              <input value={f.sku ?? ''} onChange={isNew ? onSkuChange : set('sku')} autoComplete="off"
                onFocus={() => { if (pdResults.length) setPdOpen(true); }}
                onBlur={() => setTimeout(() => setPdOpen(false), 180)}
                placeholder="เช่น BB-001 หรือพิมพ์บางส่วน"/>
              {isNew && pdOpen && pdResults.length > 0 && (
                <ul className="pd-suggest">
                  {pdResults.map(r => (
                    <li key={r.sku} onMouseDown={() => pickPd(r)}>
                      {r.image_url ? <img src={r.image_url} alt=""/> : <span className="pd-s-noimg">—</span>}
                      <span className="pd-s-sku">{r.sku}</span>
                      <span className="pd-s-nm"><NameTh c={r}/></span>
                      <span className="pd-s-br">{r.brand || ''}</span>
                    </li>
                  ))}
                </ul>
              )}
              {pdMsg && <div className={`pdlookup ${pdMsg.startsWith('ดึง') ? 'ok' : ''}`}>{pdMsg}</div>}
            </div>
          </Fld>
          <Fld label="แบรนด์">
            <input list="brand-list" value={f.brand ?? ''} onChange={set('brand')} required placeholder="พิมพ์/เลือกที่เคยใช้"/>
            <datalist id="brand-list">{brands.map(b => <option key={b} value={b}/>)}</datalist>
          </Fld>
          <Fld label="ผลิตที่ประเทศ"><CountrySelect value={f.origin_country} onChange={v => setF(prev => ({ ...prev, origin_country: v }))}/></Fld>
        </div>
        <div className="frow">
          <Fld label="หมวดสินค้า (ใต้แบรนด์)" hint="ใช้จัดกลุ่มในหน้าสินค้า — เลือกจากที่เคยใช้ หรือพิมพ์หมวดใหม่ได้เลย">
            <input list="cat-list" value={f.category ?? ''} onChange={set('category')} placeholder="เช่น Tent & Tarp / Storage"/>
            <datalist id="cat-list">{cats.map(c => <option key={c} value={c}/>)}</datalist>
          </Fld>
        </div>
        <div className="frow">
          <Fld label="ชื่อสินค้า (อังกฤษ)"><input value={f.name_en ?? ''} onChange={set('name_en')} required/></Fld>
          <Fld label="ชื่อไทยที่จะสำแดง — สินค้านี้คืออะไร?"
            hint="คำไทยกว้าง ๆ ที่ศุลกากรอ่านแล้วรู้ว่าเป็นของอะไร เช่น Land Lock = เต็นท์ · Takibi Vest = เสื้อกั๊ก — ไม่ต้องใส่ชื่อรุ่น">
            <input name="name_th" value={f.name_th ?? ''} onChange={set('name_th')} required placeholder="เช่น เต็นท์"/>
            {!hasThai(f.name_th) && hasThai(f.what_is_it) && (
              <button type="button" className="chip-use"
                onClick={() => setF(prev => ({ ...prev, name_th: String(prev.what_is_it ?? '').trim() }))}>
                ใช้คำจากช่อง “คืออะไร”: {String(f.what_is_it).trim()}
              </button>
            )}
          </Fld>
        </div>
        <div className="frow">
          <Fld label="คืออะไร ใช้ทำอะไร?"><input value={f.what_is_it ?? ''} onChange={set('what_is_it')} placeholder="เช่น เต็นท์กางนอนสำหรับแคมป์ปิ้ง 2 คน"/></Fld>
          <Fld as="div" htmlFor="f-material" label="ทำจากวัสดุอะไร?" hint={matText ? undefined : 'กดเลือกจากรายการ (ติ๊กได้หลายวัสดุ) หรือพิมพ์เพิ่มเอง — ระบบจำไว้ใช้ครั้งหน้า'}>
            <MaterialPicker inputId="f-material" value={f.material ?? ''} onChange={v => setF(prev => ({ ...prev, material: v }))}
              onPendingChange={setMatPending} onModeChange={setMatText}/>
          </Fld>
        </div>
        <MaterialProvenance item={item} material={f.material}/>
        <div className="frow">
          <Fld label="วิธีใช้งาน (สั้นๆ)"><input value={f.usage_desc ?? ''} onChange={set('usage_desc')}/></Fld>
          <Fld label="สเปคไฟ (ถ้าเป็นของไฟฟ้า)" hint="วัตต์/โวลต์/ความจุแบต — จำเป็นต่อการตีพิกัดของไฟฟ้า">
            <input value={f.power_spec ?? ''} onChange={set('power_spec')} placeholder="เช่น 10000mAh 3.7V"/>
          </Fld>
        </div>
        <Fld label="พิกัด HS 11 หลัก (broker ตีกลับแล้วกรอก)">
          <input value={f.hs_code ?? ''} onChange={set('hs_code')} placeholder="เช่น 6306.22.00.000"/>
        </Fld>
        <div className="frow">
          <label className="fld chk">
            <input type="checkbox" checked={!!f.hs_confirmed} onChange={set('hs_confirmed')}/>
            <span>broker ยืนยันพิกัดแล้ว — ครั้งหน้า reuse ได้เลย</span>
          </label>
          <label className="fld chk">
            <input type="checkbox" checked={!!f.is_dangerous_goods} onChange={set('is_dangerous_goods')}/>
            <span>เป็น DG (แบต/แก๊ส) — กระทบวิธีขนส่ง</span>
          </label>
        </div>
        {item?.id ? (
          <div className="photorow">
            {(f.photos ?? []).map((p, i) => <img key={i} src={p.url} alt={p.caption ?? ''}/>)}
            <label className="photoadd">
              ＋ รูป (อย่างน้อย 1)
              <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhoto}/>
            </label>
          </div>
        ) : f.photos?.length ? (
          <div className="photorow">
            {f.photos.map((p, i) => <img key={i} src={p.url} alt={p.caption ?? ''}/>)}
            <span className="fhint">รูปจาก Product Manager — บันทึกแล้วเพิ่ม/เปลี่ยนรูป หรือแนบไฟล์รายละเอียดได้</span>
          </div>
        ) : (
          <div className="fhint">เลือกสินค้าจาก Product Manager จะได้รูปมาเลย — หรือบันทึกก่อนแล้วค่อยแนบรูป/ไฟล์รายละเอียดเอง</div>
        )}
        {/* ไฟล์อ้างอิง — ทีมที่ดูต้นทุนได้เท่านั้น (ไฟล์จากซัพอาจมีราคาติดมา · ตรงกับ RLS ใน deploy-37) */}
        {item?.id && canCost && !MOCK && <ProductFiles classId={item.id} onBusy={setFilesBusy}/>}
        {error && <div className="ferr">{error}</div>}
        <div className="mfoot spread">
          <span className="mfoot-l">
            {item?.id && !confirmDel && (
              <button type="button" className="btn-del" onClick={() => setConfirmDel(true)}>ลบสินค้า</button>
            )}
            {item?.id && confirmDel && (
              <span className="del-confirm">
                ลบสินค้านี้ออกจากคลัง?
                <button type="button" className="btn-del" disabled={busy} onClick={del}>{busy ? 'กำลังลบ…' : 'ยืนยันลบ'}</button>
                <button type="button" className="btn2 sm" onClick={() => setConfirmDel(false)}>ไม่ลบ</button>
              </span>
            )}
            {item?.id && onOpenPermit && canCost && !confirmDel && (
              <button type="button" className="btn2"
                disabled={filesBusy || dirty}
                title={dirty ? 'บันทึกที่แก้ไว้ก่อน แล้วค่อยไปหน้าเอกสารยื่นขอใบอนุญาต'
                  : 'ทำเอกสารข้อมูลสินค้าเพื่อยื่นขอใบอนุญาต สมอ./เกษตร'}
                onClick={() => { onClose(); onOpenPermit({ ...item, photos: f.photos ?? item.photos }); }}>
                เอกสารยื่นขอใบอนุญาต (สมอ./เกษตร) →</button>
            )}
          </span>
          <span className="mfoot-r">
            <button type="button" className="btn2" onClick={safeClose}>ปิด</button>
            <button className="btn1" disabled={busy || filesBusy}>
              {busy ? 'กำลังบันทึก…' : filesBusy ? 'รอแนบไฟล์…' : 'บันทึก'}</button>
          </span>
        </div>
      </form>
      )}
    </Modal>
  );
}

// map หัวคอลัมน์ (ไทย/อังกฤษ) → field
const COLMAP = {
  sku: 'sku', รหัส: 'sku', รหัสสินค้า: 'sku',
  brand: 'brand', แบรนด์: 'brand', ยี่ห้อ: 'brand',
  name_en: 'name_en', name: 'name_en', ชื่อ: 'name_en', ชื่ออังกฤษ: 'name_en', 'ชื่อสินค้า': 'name_en',
  name_th: 'name_th', ชื่อไทย: 'name_th', คำแปล: 'name_th', 'ชื่อไทยสำแดง': 'name_th',
  what_is_it: 'what_is_it', คืออะไร: 'what_is_it', 'ใช้ทำอะไร': 'what_is_it',
  material: 'material', วัสดุ: 'material', 'ทำจาก': 'material', 'ทำจากอะไร': 'material',
  usage_desc: 'usage_desc', 'การใช้งาน': 'usage_desc', 'วิธีใช้': 'usage_desc',
  origin_country: 'origin_country', ประเทศ: 'origin_country', 'ถิ่นกำเนิด': 'origin_country', 'ผลิตที่': 'origin_country',
  power_spec: 'power_spec', 'สเปคไฟ': 'power_spec',
  hs_code: 'hs_code', hs: 'hs_code', พิกัด: 'hs_code',
};

// CSV parser (รองรับ field มี comma ในเครื่องหมายคำพูด)
function parseCsv(text) {
  const rows = [];
  let row = [], cell = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (q) {
      if (c === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') q = false;
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; }
    else if (c === '\r') { /* skip */ }
    else cell += c;
  }
  if (cell.length || row.length) { row.push(cell); rows.push(row); }
  return rows.filter(r => r.some(x => x.trim()));
}

function fileToRecords(text) {
  const rows = parseCsv(text);
  if (rows.length < 2) return [];
  const headers = rows[0].map(h => COLMAP[h.trim().toLowerCase()] ?? COLMAP[h.trim()] ?? null);
  return rows.slice(1).map(r => {
    const rec = {};
    headers.forEach((f, i) => { if (f && (r[i] ?? '').trim()) rec[f] = r[i].trim(); });
    if (rec.origin_country) rec.origin_country = rec.origin_country.toUpperCase().slice(0, 2);
    // นำเข้าไฟล์ = คลังพิกัดที่เคยใช้แล้ว → มี HS = ถือว่ายืนยันแล้ว (reuse ได้ทันที)
    if (rec.hs_code) rec.hs_confirmed = true;
    return rec;
  }).filter(rec => rec.name_en || rec.name_th || rec.sku);
}

const TEMPLATE = 'sku,brand,name_en,name_th,what_is_it,material,usage_desc,origin_country,power_spec,hs_code\n' +
  'SP-TENT-01,Snow Peak,2-person tent,เต็นท์ 2 คน,เต็นท์กางนอนแคมป์ปิ้ง,ผ้าโพลีเอสเตอร์+เสาอลูมิเนียม,กางนอนกลางแจ้ง,JP,,6306.22.00.000\n';

// ตัวอย่างสำหรับดูหน้าตาโหมด MOCK (ไม่มี DB) — หลายแบรนด์เพื่อทดสอบ rail
const MOCK_CLASSES = [
  { id: 'm1', sku: 'TC-095-01', brand: 'TOKYOCRAFTS', category: 'Tent & Tarp', name_en: 'WINGFORT OUTER TENT', name_th: 'เต็นท์นอกวิงฟอร์ต', hs_code: '63062200', hs_confirmed: true, what_is_it: 'เต็นท์', material: 'ผ้าโพลีเอสเตอร์', usage_desc: 'กางนอน', origin_country: 'JP', photos: [] },
  // m2 = ค่าโครงสร้าง + ที่มาจาก AI (ช่องวัสดุเป็นข้อความ ไม่แตกชิป) · m5 = ชิปหลายอันแบบเดิม · m3 = ค่าโครงสร้างที่พิมพ์มือ (ไม่มีที่มา)
  { id: 'm2', sku: 'TC-153', brand: 'TOKYOCRAFTS', category: 'Storage', name_en: 'Light Soft Container', name_th: 'กล่องผ้าเบา', hs_code: '42029290', hs_confirmed: true, photos: [],
    material: 'ตัวเสื้อ: ฝ้าย 66%, โพลีเอสเตอร์ 34%; ริบ: ฝ้าย 95%, สแปนเด็กซ์ 5%', material_en: 'BODY: COTTON 66%, POLYESTER 34%; RIB: COTTON 95%, SPANDEX 5%',
    material_source_url: 'https://tokyocrafts.jp/items/light-soft-container', material_research_id: 'r-appr' },
  { id: 'm3', sku: 'TC-282', brand: 'TOKYOCRAFTS', category: 'Storage', name_en: 'AIRDRY MULTI POUCH', name_th: 'กระเป๋าอเนกประสงค์', hs_code: '42029290', hs_confirmed: true, photos: [],
    material: 'โพลีเอสเตอร์ 100%', material_research_id: 'r-appr-ed' },
  { id: 'm4', sku: 'TC-151', brand: 'TOKYOCRAFTS', category: 'Storage', name_en: 'Tissue Case', name_th: 'กล่องทิชชู่', hs_code: '42029290', hs_confirmed: true, photos: [] },
  { id: 'm5', sku: 'TC-036', brand: 'TOKYOCRAFTS', category: 'Furniture', name_en: 'BUCKETSHELF MESH', name_th: 'ชั้นตะแกรง', hs_code: '94038990', hs_confirmed: true, photos: [], material: 'เหล็ก, ตาข่าย' },
  { id: 'm6', sku: 'TC-202', brand: 'TOKYOCRAFTS', category: 'Furniture', name_en: 'Gear Bunker', name_th: 'กล่องเก็บอุปกรณ์', hs_code: '76151090', hs_confirmed: true, photos: [] },
  { id: 'm7', sku: 'NG-32-CB', brand: 'Nalgene', category: 'Bottle', name_en: 'Wide Mouth 32oz', name_th: 'ขวดน้ำปากกว้าง 32oz', hs_code: '39241000', hs_confirmed: true, photos: [] },
  { id: 'm8', sku: 'NG-16-CB', brand: 'Nalgene', category: 'Bottle', name_en: 'Narrow Mouth 16oz', name_th: 'ขวดน้ำปากแคบ 16oz', hs_code: '39241000', hs_confirmed: false, photos: [] },
  { id: 'm9', sku: 'NG-KIDS', brand: 'Nalgene', name_en: 'Kids Bottle', name_th: 'ขวดน้ำเด็ก', hs_code: '', hs_confirmed: false, photos: [] },
  { id: 'm10', sku: 'SP-TENT', brand: 'Snow Peak', category: 'Tent & Tarp', name_en: 'Amenity Dome', name_th: 'เต็นท์โดม', hs_code: '63062200', hs_confirmed: true, photos: [] },
  { id: 'm11', sku: 'SP-IGT', brand: 'Snow Peak', name_en: 'IGT Frame', name_th: 'โครงโต๊ะ IGT', hs_code: '', hs_confirmed: false, photos: [] },
  { id: 'm12', sku: 'MISC-01', brand: null, name_en: 'Unbranded sample', name_th: 'สินค้าไม่ระบุแบรนด์', hs_code: '', hs_confirmed: false, photos: [] },
];

export default function Classifications({ onOpenPermit, focus, onFocusConsumed, onView, canCost = false, canAi = false, onOpenMaterials }) {
  const [items, setItems] = useState([]);
  const [tree, setTree] = useState([]);               // [{brand, category, n}] — rail 2 ชั้น
  const [selBrand, setSelBrand] = useState(null);     // null = ทั้งหมด · db.NO_BRAND = ไม่ระบุ · อื่น = แบรนด์นั้น
  const [selCat, setSelCat] = useState(null);         // null = ทุกหมวดของแบรนด์ · db.NO_CAT = ยังไม่จัดหมวด
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);       // item | 'new'
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const [brandList, setBrandList] = useState([]);      // ชื่อแบรนด์ (datalist ใน EditModal)
  const [pdOpen, setPdOpen] = useState(false);
  const [elsewhere, setElsewhere] = useState(null);  // ค้นในแบรนด์/หมวดที่เลือกไม่เจอ → { n, where: 'cat' | 'brand' }
  const [searchAlt, setSearchAlt] = useState(null);  // คำที่พิมพ์ไม่เจอ แต่แปลงจากแป้นไทยแล้วเจอ → คำอังกฤษนั้น
  const fileRef = useRef(null);
  // หาวัสดุด้วย AI: เลือกการ์ดแล้วส่งเข้าหน้าต่างดูก่อนลงคิว (ยังไม่รัน deploy-38 = ไม่โชว์ · แบบเดียวกับ sel ใน PdSyncModal)
  //   เลือกค้างข้ามตัวกรองได้ · ออกจากหน้า = ล้าง (หน้าเริ่มใหม่ตาม key ใน App)
  const aiOn = useMaterialsReady(canAi);
  const [picked, setPicked] = useState(() => new Map());   // id → { id, sku, brand }
  const [aiOpen, setAiOpen] = useState(false);
  const [aiQueued, setAiQueued] = useState(false);
  const togglePick = (c) => setPicked(prev => {
    const n = new Map(prev);
    if (n.has(c.id)) n.delete(c.id); else n.set(c.id, { id: c.id, sku: c.sku, brand: c.brand });
    return n;
  });
  const pickNoMaterial = () => setPicked(prev => {
    const n = new Map(prev);
    for (const c of items) if (!String(c.material ?? '').trim()) n.set(c.id, { id: c.id, sku: c.sku, brand: c.brand });
    return n;
  });
  // ค้นหา (28 ต.ค. 69 "ใส่ sku แล้ว search ไม่เจอ" — ดูต้นเหตุใน data.js classSearchTerms):
  //   reqRef = เลขคำขอล่าสุด → คำตอบของคำค้นเก่าที่มาช้ากว่า (เช่นรายการ 500 ใบตอนล้างช่อง) ห้ามทับผลค้นใหม่
  //   typeTimer = รอพิมพ์จบ 250ms ค่อยค้น (เดิมยิงทุกตัวอักษร "trucker" = 7 ครั้ง)
  const reqRef = useRef(0);
  const typeTimer = useRef(null);
  useEffect(() => () => clearTimeout(typeTimer.current), []);

  // เปิดหน้าตามที่สั่งมา { q, brand, cat } — จาก URL ตอนรีเฟรช หรือจากแผง "ยังไม่ครบ" ในใบข้อมูลสินค้า (q = รหัส)
  //   เติมช่องค้นหา/แบรนด์/หมวด **แล้วค้นจริง** แล้วล้างธงทิ้ง
  //   (ล้างทันทีไม่งั้นพิมพ์ค้นหาอย่างอื่นแล้วโดนเด้งกลับมาค่าเดิมทุก render)
  // 🐛 เดิมใส่รหัสในช่องแต่ไม่ได้เรียก load → หน้าโชว์ 500 ใบล่าสุดทั้งที่ช่องค้นมีรหัส (เจอใน log 28 ต.ค.:
  //   เปิดหน้าแล้วอีก 10–25 วิ คนต้องวางรหัสค้นเองซ้ำ)
  useEffect(() => {
    if (!focus) return;
    clearTimeout(typeTimer.current);
    setSearch(focus.q); setSelBrand(focus.brand); setSelCat(focus.cat);
    load(focus.q, focus.brand, focus.cat);
    onFocusConsumed?.();
  }, [focus]);

  const mockMatch = (c, q, brand, cat) =>
    (brand == null || (brand === db.NO_BRAND ? !c.brand : c.brand === brand)) &&
    (cat == null || (cat === db.NO_CAT ? !c.category : c.category === cat)) &&
    (!q || [c.sku, c.name_en, c.name_th, c.brand].some(v => (v || '').toLowerCase().includes(q.toLowerCase())));
  const mockTree = () => {
    const map = new Map();
    for (const c of MOCK_CLASSES) {
      const k = JSON.stringify([c.brand || null, c.category || null]);
      map.set(k, (map.get(k) || 0) + 1);
    }
    return [...map].map(([k, n]) => {
      const [b, cat] = JSON.parse(k);
      return { brand: b, category: cat, n };
    });
  };

  const load = async (q = search, brand = selBrand, cat = selCat) => {
    const my = ++reqRef.current;
    onView?.({ q, brand, cat });   // สิ่งที่กำลังแสดง → URL (รีเฟรชแล้วได้แบรนด์/หมวด/คำค้นเดิม)
    if (MOCK) { setItems(MOCK_CLASSES.filter(c => mockMatch(c, q, brand, cat))); return; }
    try {
      let list = await db.listClassifications(q, brand, cat);
      if (my !== reqRef.current) return;              // มีคำค้นใหม่กว่าแล้ว — ทิ้งคำตอบนี้
      // ไม่เจอเลย → ลองแปลงจากแป้นไทย ("ะฟาริร" → takibi) · คำไทยที่ค้นเจออยู่แล้วไม่ต้องแปลง
      const alt = list.length ? null : db.classSearchTerms(q).alt;
      let used = null;
      if (alt) {
        const l2 = await db.listClassifications(alt, brand, cat);
        if (my !== reqRef.current) return;
        if (l2.length) { list = l2; used = alt; }
      }
      setItems(list); setSearchAlt(used); setError(null); setPending(false); setElsewhere(null);
      if (!list.length && db.classSearchTerms(q).q && (brand != null || cat != null)) {
        const count = async (b) => (await db.countClassSearch(q, b).catch(() => 0)) || (alt ? await db.countClassSearch(alt, b).catch(() => 0) : 0);
        // เลือกหมวดไว้ → ดูในแบรนด์เดียวกันก่อน (ของอาจอยู่หมวดอื่นของแบรนด์นี้ ไม่ใช่แบรนด์อื่น)
        const inBrand = cat != null && brand != null ? await count(brand) : 0;
        const found = inBrand ? { n: inBrand, where: 'cat' } : { n: await count(null), where: 'brand' };
        if (my === reqRef.current && found.n) setElsewhere(found);
      }
    } catch (e) {
      if (my !== reqRef.current) return;
      if (isSchemaPending(e)) setPending(true); else setError(e.message ?? String(e));
    }
  };
  const loadTree = async () => {
    if (MOCK) { setTree(mockTree()); return; }
    try { setTree(await db.classificationTree()); } catch { /* เงียบ — rail แค่ไม่ขึ้น count */ }
  };
  useEffect(() => {
    if (!focus) load('');   // เปิดมาพร้อมค่าที่สั่ง → effect ข้างบนค้นให้แล้ว (ถ้ายิง '' ด้วย อาจมาช้ากว่าแล้วทับ)
    loadTree();
    if (!MOCK) db.knownBrands().then(setBrandList).catch(() => {});
  }, []);

  // rail ชั้นบน = แบรนด์ (รวมจาก tree) · ชั้นล่าง = หมวดของแบรนด์ที่เลือก
  const brands = (() => {
    const map = new Map();
    for (const t of tree) { const b = t.brand ?? null; map.set(b, (map.get(b) || 0) + t.n); }
    return [...map].map(([brand, n]) => ({ brand, n })).sort((a, b) => b.n - a.n);
  })();
  const cats = selBrand == null ? [] : tree
    .filter(t => (selBrand === db.NO_BRAND ? t.brand == null : t.brand === selBrand))
    .map(t => ({ category: t.category ?? null, n: t.n }))
    .sort((a, b) => b.n - a.n);

  const selectBrand = (b) => { clearTimeout(typeTimer.current); setSelBrand(b); setSelCat(null); load(search, b, null); };
  const selectCat = (c) => { clearTimeout(typeTimer.current); setSelCat(c); load(search, selBrand, c); };
  const onSearch = (v) => {
    setSearch(v);
    clearTimeout(typeTimer.current);
    typeTimer.current = setTimeout(() => load(v, selBrand, selCat), 250);
  };
  const refreshAll = () => { load(); loadTree(); };   // หลังเพิ่ม/ลบ/นำเข้า — จำนวนเปลี่ยน
  const total = brands.reduce((s, b) => s + b.n, 0);
  const mockHint = () => { setImportMsg('โหมด MOCK — ต่อ Supabase จริงเพื่อเพิ่ม/แก้/ลบสินค้า'); setTimeout(() => setImportMsg(null), 2500); };

  const onImport = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setImportMsg('กำลังอ่านไฟล์…'); setError(null);
    try {
      const recs = fileToRecords(await file.text());
      if (!recs.length) throw new Error('ไม่พบข้อมูล — หัวคอลัมน์ต้องมี ชื่อสินค้า/SKU (ดาวน์โหลดเทมเพลตดูตัวอย่าง)');
      const r = await db.bulkClassifications(recs);
      setImportMsg(`นำเข้าสำเร็จ — ใบใหม่ ${r.added} · แก้ใบเดิม ${r.updated}`
        + (r.skippedTh ? ` · ช่องชื่อไทยที่ไม่ใช่ภาษาไทย ${r.skippedTh} แถวไม่ได้ใส่ (ชื่อไทยต้องบอกว่าสินค้าคืออะไร เช่น เต็นท์)` : '')
        + (r.noSku ? ` · ไม่มีรหัสสินค้า ${r.noSku} แถว — สร้างเป็นใบใหม่ (นำเข้าไฟล์เดิมซ้ำจะได้ใบซ้ำ)` : '')
        + ' · ช่องที่ว่างในไฟล์ไม่ลบค่าเดิม');
      refreshAll();
      setTimeout(() => setImportMsg(null), 8000);
    } catch (er) { setImportMsg(null); setError(er.message ?? String(er)); }
  };

  const downloadTemplate = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob(['﻿' + TEMPLATE], { type: 'text/csv' }));
    a.download = 'template_สินค้า.csv'; a.click(); URL.revokeObjectURL(a.href);
  };

  if (pending) return <DbPending onRetry={() => load(search, selBrand, selCat)}/>;   // ลองใหม่ด้วยตัวกรองเดิม (URL ไม่หาย)

  return (
    <div className="page">
      <input ref={fileRef} type="file" accept=".csv,.txt" style={{ display: 'none' }} onChange={onImport}/>
      <div className="page-h">
        <h2>สินค้า & HS Master</h2>
        <span className="meta">SKU ใหม่ → ทำ dossier ครั้งเดียว → broker ตีพิกัด → ครั้งหน้าอัตโนมัติ</span>
        <input className="search" placeholder="ค้น SKU / ชื่อ / แบรนด์…" value={search}
          onChange={e => onSearch(e.target.value)}/>
        <button className="btn2" onClick={() => MOCK ? mockHint() : setPdOpen(true)}>ดึงจาก Product Manager</button>
        <button className="btn2" onClick={downloadTemplate}>เทมเพลต</button>
        <button className="btn2" onClick={() => MOCK ? mockHint() : fileRef.current?.click()}>นำเข้าไฟล์</button>
        <button className="btn1" onClick={() => setEditing('new')}>＋ เพิ่มสินค้า</button>
      </div>
      {searchAlt && (
        <div className="search-alt">ไม่เจอคำที่พิมพ์ — แป้นพิมพ์เป็นภาษาไทยอยู่หรือเปล่า? ผลด้านล่างคือของที่ตรงกับ <b>“{searchAlt}”</b></div>
      )}
      {MOCK && <div className="importmsg">โหมด MOCK — ข้อมูลตัวอย่างไว้ดูหน้าตา (ของจริงเห็นหลัง login)</div>}
      {importMsg && <div className="importmsg">{importMsg}</div>}
      {error && <div className="ferr">{error}</div>}
      {aiOn && (
        <div className="pickbar">
          <span className="pickbar-n">เลือกอยู่ <b>{picked.size}</b> รายการ</span>
          <button type="button" className="mini" onClick={pickNoMaterial}>เลือกทุกใบในหน้านี้ที่ยังไม่มีวัสดุ</button>
          <button type="button" className="mini" disabled={!picked.size} onClick={() => setPicked(new Map())}>ล้าง</button>
          <button type="button" className="btn1 sm" disabled={!picked.size} onClick={() => { setAiQueued(false); setAiOpen(true); }}>หาวัสดุด้วย AI</button>
          {!picked.size && <span className="fhint">ติ๊กมุมการ์ดสินค้าที่อยากให้ช่วยหาวัสดุ — ระบบบอกจำนวนรุ่นและค่าใช้จ่ายก่อนเริ่มเสมอ</span>}
        </div>
      )}

      <div className="classlayout">
        <aside className="brandrail">
          <div className="brl-h">แบรนด์</div>
          <button className={`brl-item ${selBrand == null ? 'on' : ''}`} onClick={() => selectBrand(null)}>
            <span className="brl-n">ทั้งหมด</span><span className="brl-c">{total}</span>
          </button>
          {brands.map(b => {
            const bval = b.brand ?? db.NO_BRAND;
            const on = selBrand === bval;
            return (
              <div key={bval}>
                <button className={`brl-item ${on && selCat == null ? 'on' : ''}`}
                  onClick={() => selectBrand(on ? null : bval)}>
                  <span className="brl-n">{b.brand || 'ไม่ระบุแบรนด์'}</span><span className="brl-c">{b.n}</span>
                </button>
                {on && cats.length > 1 && cats.map(c => {
                  const cval = c.category ?? db.NO_CAT;
                  return (
                    <button key={cval} className={`brl-item brl-sub ${selCat === cval ? 'on' : ''}`}
                      onClick={() => selectCat(selCat === cval ? null : cval)}>
                      <span className="brl-n">{c.category || 'ยังไม่จัดหมวด'}</span><span className="brl-c">{c.n}</span>
                    </button>
                  );
                })}
              </div>
            );
          })}
          {!brands.length && <div className="brl-empty">ยังไม่มีแบรนด์</div>}
        </aside>

        <div className="classmain">
          <div className="classgrid">
            {items.map(c => {
              const comp = completeness(c);
              return (
                <div key={c.id} className={`classcell ${picked.has(c.id) ? 'on' : ''}`}>
                {/* ช่องติ๊กอยู่นอกปุ่ม — input ในปุ่มผิดกติกา HTML และกดแล้วจะเปิดหน้าแก้ไขไปด้วย */}
                {aiOn && (
                  <label className="classpick" title="เลือกรายการนี้เพื่อหาวัสดุด้วย AI">
                    <input type="checkbox" checked={picked.has(c.id)} onChange={() => togglePick(c)} aria-label={`เลือก ${c.sku || itemName(c)}`}/>
                  </label>
                )}
                <button className="classcard" onClick={() => setEditing(c)}>
                  {(c.photos ?? [])[0]
                    ? <img src={c.photos[0].url} alt=""/>
                    : <div className="nophoto">ไม่มีรูป</div>}
                  <div className="cc-b">
                    <div className="cc-n" title={itemTitle(c)}>{itemName(c)}</div>
                    {itemNameTh(c)
                      ? <div className="cc-th">ไทย: {itemNameTh(c)}</div>
                      : thaiNameState(c) === 'empty' ? <div className="cc-th miss">ยังไม่มีชื่อไทย</div>
                      : thaiNameState(c) === 'english' ? <div className="cc-th miss">ชื่อไทยยังเป็นภาษาอังกฤษ</div>
                      : null}
                    <div className="cc-m">{c.brand || 'ไม่ระบุแบรนด์'}{c.category ? ` › ${c.category}` : ''}{c.sku ? ` · ${c.sku}` : ''}</div>
                    <div className="cc-tags">
                      {c.hs_code
                        ? <span className={`hschip ${c.hs_confirmed ? 'ok' : ''}`}>{c.hs_code}{c.hs_confirmed ? ' ✓' : ' (รอยืนยัน)'}</span>
                        : <span className="hschip miss">ยังไม่มี HS</span>}
                      {c.is_dangerous_goods && <span className="hschip dg">DG</span>}
                      <span className="hschip">{comp.have}/{comp.total}</span>
                      {aiOn && c.material_research_id && (typedByPerson(c)
                        ? <span className="hschip">วัสดุ: คนพิมพ์เอง</span>
                        : <span className="hschip ok">วัสดุจาก AI</span>)}
                      {aiOn && !String(c.material ?? '').trim() && <span className="hschip miss">ยังไม่มีวัสดุ</span>}
                    </div>
                  </div>
                </button>
                </div>
              );
            })}
            {!items.length && (
              <div className="placeholder" style={{ gridColumn: '1/-1' }}>
                <h2>{search || selBrand != null ? 'ไม่พบสินค้าที่ตรง' : 'ยังไม่มีสินค้าใน master'}</h2>
                <p>{search ? (elsewhere ? (elsewhere.where === 'cat'
                      ? `ไม่มีในหมวดที่เลือก แต่เจอในหมวดอื่นของแบรนด์นี้ ${elsewhere.n} รายการ`
                      : `ไม่มีในแบรนด์ที่เลือก แต่เจอในแบรนด์อื่น ${elsewhere.n} รายการ`)
                    : selBrand != null || selCat != null ? 'ลองคำค้นอื่น หรือกด "ทั้งหมด" เพื่อค้นทุกแบรนด์'
                    : 'ลองตรวจรหัส/คำค้นอีกครั้ง — ถ้าแป้นพิมพ์เป็นภาษาไทยอยู่ ลองสลับเป็นอังกฤษแล้วพิมพ์ใหม่')
                  : selBrand != null ? 'แบรนด์นี้ยังไม่มีสินค้า'
                  : 'เพิ่มสินค้าที่จะนำเข้า — ชื่อไทยสำแดง / วัสดุ / การใช้งาน / รูป คือสิ่งที่ broker ต้องใช้'}</p>
                {search && elsewhere && (elsewhere.where === 'cat'
                  ? <button className="btn1" onClick={() => selectCat(null)}>ค้นทั้งแบรนด์ ({elsewhere.n} รายการ)</button>
                  : <button className="btn1" onClick={() => selectBrand(null)}>ค้นทุกแบรนด์ ({elsewhere.n} รายการ)</button>)}
              </div>
            )}
          </div>
          {items.length >= 500 && (
            <div className="fhint" style={{ textAlign: 'center', marginTop: 12 }}>
              แสดง 500 ตัวแรก — เลือกแบรนด์ด้านซ้าย หรือค้นหา เพื่อดูให้เจาะจง
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditModal key={editing === 'new' ? 'new' : editing.id} item={editing === 'new' ? null : editing} brands={brandList}
          cats={[...new Set(tree.map(t => t.category).filter(Boolean))].sort()}
          canCost={canCost} onClose={() => setEditing(null)}
          onSaved={refreshAll} onDeleted={refreshAll} onOpenPermit={onOpenPermit}/>
      )}
      {aiOpen && (
        <MaterialEnqueueModal ids={[...picked.keys()]} onOpenReview={onOpenMaterials}
          onDone={() => setAiQueued(true)}
          onClose={() => { setAiOpen(false); if (aiQueued) setPicked(new Map()); }}/>
      )}
      {pdOpen && (
        <PdSyncModal onClose={() => setPdOpen(false)}
          onImported={(r) => { setImportMsg(`ดึงจาก Product Manager — ใบใหม่ ${r.added} · เติมช่องที่ว่างให้ใบเดิม ${r.updated}`); refreshAll(); setTimeout(() => setImportMsg(null), 4000); }}/>
      )}
    </div>
  );
}

// ── ดึงสินค้าจาก Product Manager (catalog ของ Purim) — ค้น เลือก แล้ว sync เข้าคลัง ──────────
function PdSyncModal({ onClose, onImported }) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState([]);
  const [sel, setSel] = useState({});          // sku → record
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const run = async (t) => {
    setBusy(true); setError(null);
    try { setResults(await db.searchPdProducts(t)); } catch (e) { setError(e.message ?? String(e)); }
    setBusy(false);
  };
  useEffect(() => { run(''); }, []);

  const toggle = (r) => setSel(s => {
    const n = { ...s }; if (n[r.sku]) delete n[r.sku]; else n[r.sku] = r; return n;
  });

  const importSel = async () => {
    const recs = Object.values(sel).map(r => ({
      sku: r.sku, name_th: hasThai(r.name_th) ? r.name_th : null,   // ช่องไทยของ PD มักเป็นชื่ออังกฤษ — ไม่ก๊อป
      name_en: r.name_en || r.name_th || r.sku,   // name_en บังคับ not-null → ใช้ชื่อไทย/sku แทน
      ...db.pdBrandCategory(r),                   // brand สะอาด + หมวดจาก PD
      photos: r.image_url ? [{ url: r.image_url, caption: 'PD' }] : null,   // ดึงรูปจาก PD มาด้วย
    })).filter(r => r.sku);
    if (!recs.length) return;
    setBusy(true); setError(null);
    // ใบที่มีอยู่แล้ว = เติมเฉพาะช่องที่ว่าง (รูป/แบรนด์/หมวด) ไม่แตะชื่อ — เดิมทับชื่อไทยเป็นว่าง (รีวิว 29 ต.ค. 69)
    try { const r = await db.bulkClassifications(recs, { onlyFillEmpty: true }); onImported(r); onClose(); }
    catch (e) { setError(e.message ?? String(e)); setBusy(false); }
  };

  return (
    <Modal title="ดึงสินค้าจาก Product Manager" sub="catalog เดียวกับ Product Manager (PD ของ Purim) — เลือกมาใช้เป็นสินค้าใน INBOUNDpoint (พิกัด HS เติมทีหลัง)" onClose={onClose} wide>
      <div className="pd-search">
        <input value={term} onChange={e => setTerm(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && run(term)} placeholder="ค้น SKU / ชื่อ / แบรนด์…"/>
        <button className="btn2" onClick={() => run(term)}>ค้น</button>
      </div>
      {error && <div className="ferr">{error}</div>}
      <div className="pd-list">
        {busy && !results.length ? <div className="jr-empty">กำลังค้น…</div> :
          results.map(r => (
            <label className={`pd-row ${sel[r.sku] ? 'on' : ''}`} key={r.sku}>
              <input type="checkbox" checked={!!sel[r.sku]} onChange={() => toggle(r)}/>
              {r.image_url ? <img className="pd-thumb" src={r.image_url} alt=""/> : <span className="pd-thumb none">—</span>}
              <span className="pd-sku">{r.sku}</span>
              <span className="pd-nm"><NameTh c={r}/></span>
              <span className="pd-br">{r.brand || ''}</span>
            </label>
          ))}
        {!busy && !results.length && <div className="jr-empty">ไม่พบ — ลองคำค้นอื่น</div>}
      </div>
      <div className="mfoot spread">
        <span className="meta">เลือก {Object.keys(sel).length} รายการ</span>
        <span className="mfoot-r">
          <button type="button" className="btn2" onClick={onClose}>ปิด</button>
          <button className="btn1" disabled={busy || !Object.keys(sel).length} onClick={importSel}>
            {busy ? 'กำลังดึง…' : `เพิ่มที่เลือก (${Object.keys(sel).length})`}
          </button>
        </span>
      </div>
    </Modal>
  );
}
